import { qs, qsa, on, lockScroll } from '../lib/dom.js';

// Progressive-enhancement single-select for the catalog sort control.
//
// Server markup (see template-parts/catalog/toolbar.php):
//   <div class="ap-sort" data-component="sort-dropdown">
//     <select class="ap-sort__native" data-role="sort-select" name="sort">…</select>
//     <button class="ap-sort__trigger" data-role="sort-trigger">
//       <svg …/> <span data-role="sort-value"></span> <svg caret/>
//     </button>
//     <div class="ap-sort__panel" data-role="sort-panel" role="listbox" hidden>
//       <button class="ap-sort__opt" role="option" data-value="…" data-label="…">…</button>…
//     </div>
//   </div>
//
// The native <select> stays in the DOM as the single source of truth AND the
// no-JS fallback: choosing a custom option writes `select.value` and dispatches
// a native `change`, so catalog.js and the filter-panel URL writer react exactly
// as they did with the plain select. When JS is off, the native select is still
// visible and functional; enhancing hides it and reveals the custom trigger.

function isMobile() {
  return window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
}

export function initSortDropdown(root) {
  const el = root || qs('[data-component="sort-dropdown"]');
  if (!el || el.dataset.sortInit) return;

  const native = qs('[data-role="sort-select"]', el);
  const trigger = qs('[data-role="sort-trigger"]', el);
  const panel = qs('[data-role="sort-panel"]', el);
  const valueEl = qs('[data-role="sort-value"]', el);
  if (!native || !trigger || !panel) return;

  el.dataset.sortInit = '1';

  // Enhance: the custom UI takes over. The native select keeps its value and
  // remains the change source, but leaves the tab order / a11y tree so screen
  // readers see one control (the custom listbox), not two.
  el.classList.add('is-enhanced');
  native.setAttribute('tabindex', '-1');
  native.setAttribute('aria-hidden', 'true');

  const options = qsa('[role="option"]', panel);
  let backdrop = null;
  let offKeydown = null;
  let offDocClick = null;

  function labelFor(value) {
    const opt = options.find((o) => o.dataset.value === value);
    return opt ? (opt.dataset.label || opt.textContent.trim()) : '';
  }

  // Mirror the native select's current value into the trigger label + the
  // option checkmarks. Called on init, after a pick, and whenever `change`
  // fires from elsewhere (catalog.js adopts `?sort=` from the URL on load).
  function syncFromNative() {
    const v = native.value;
    if (valueEl) valueEl.textContent = labelFor(v);
    options.forEach((o) => {
      const isSel = o.dataset.value === v;
      o.setAttribute('aria-selected', String(isSel));
      o.classList.toggle('is-selected', isSel);
    });
  }

  function open() {
    if (el.classList.contains('is-open')) return;
    el.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;

    if (isMobile()) {
      lockScroll(true);
      backdrop = document.createElement('div');
      backdrop.className = 'ap-sort__backdrop';
      document.body.appendChild(backdrop);
      on(backdrop, 'click', close);
    }

    const sel = options.find((o) => o.getAttribute('aria-selected') === 'true') || options[0];
    if (sel) sel.focus();

    offKeydown = on(document, 'keydown', onKeydown);
    // Defer the outside-click listener so the opening click doesn't self-close.
    setTimeout(() => { offDocClick = on(document, 'click', onDocClick); }, 0);
  }

  function close() {
    if (!el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
    if (backdrop) { backdrop.remove(); backdrop = null; }
    lockScroll(false);
    if (offKeydown) { offKeydown(); offKeydown = null; }
    if (offDocClick) { offDocClick(); offDocClick = null; }
  }

  function onDocClick(e) {
    if (!el.contains(e.target)) close();
  }

  function choose(value) {
    if (native.value !== value) {
      native.value = value;
      native.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncFromNative();
    close();
    trigger.focus();
  }

  function onKeydown(e) {
    const idx = options.indexOf(document.activeElement);
    switch (e.key) {
      case 'Escape':
        e.preventDefault(); close(); trigger.focus(); break;
      case 'ArrowDown':
        e.preventDefault(); (options[idx + 1] || options[0]).focus(); break;
      case 'ArrowUp':
        e.preventDefault(); (options[idx - 1] || options[options.length - 1]).focus(); break;
      case 'Home':
        e.preventDefault(); options[0].focus(); break;
      case 'End':
        e.preventDefault(); options[options.length - 1].focus(); break;
      case 'Enter':
      case ' ':
        if (idx > -1) { e.preventDefault(); choose(options[idx].dataset.value); }
        break;
      default:
        break;
    }
  }

  on(trigger, 'click', (e) => {
    e.preventDefault();
    if (el.classList.contains('is-open')) close(); else open();
  });
  options.forEach((o) => on(o, 'click', (e) => { e.preventDefault(); choose(o.dataset.value); }));

  // If the value changes elsewhere (URL adoption on load, saved-search apply),
  // keep the custom trigger + checkmarks in step.
  on(native, 'change', syncFromNative);

  syncFromNative();
}
